import React, { ReactNode } from 'react';
import Clamp from 'react-multiline-clamp';
import { ChevronDownDoubleCircleIcon, ChevronDownIcon, ChevronUpDoubleCircleIcon, ChevronUpIcon } from '@navikt/aksel-icons';
import { BodyShort, Button, Checkbox, ColumnHeaderProps, ExpandableRowProps, HelpText, HStack, Pagination, Select, Table, TableProps } from '@navikt/ds-react';
import { store } from '@/app/_components/store/store';
import { hjelpetekstAriaLabel } from '@/utils/formatting/tekst';
import { css } from '@emotion/css';

//Todo: Ved gruppering er index for elementene lokal index per gruppe, ikke index i origianal array.

type IfFirstThenBoth<First extends { [K in keyof First]: boolean }, Second = any> = ({ [K in keyof First]: false } & Second) // First not allowed → only Second
| (First & Required<Second>);
type SortedTableState<T extends any[] = any[]> = {
  orderBy: string;
  direction: 'ascending' | 'descending' | undefined;
  page: number;
  rowsPerPage: number;
  expandedGroups: Array<boolean>;
  selectedRows: T;
  selectedIds: string[] | number[];
};
export type SortedTableProps<T extends Array<unknown>> = SortedTableState<T> & TableProps;
type StableProps<T extends unknown[]> = {
  fields: {
    label?: React.ReactNode;
    hjelpetekst?: React.ReactNode;
    labelStyle?: React.CSSProperties;
    hideLabel?: boolean;
    getValue: (data: T[0], index: number, id: string | number) => React.ReactNode;
    sortValue?: (data: T[0]) => string | number | undefined | null;
    sortable?: boolean;
    maxLines?: number;
    width?: string | number;
    minWidth?: string | number;
    align?: ColumnHeaderProps['align'];
    header?: boolean;
    sticky?: boolean;
    hidden?: boolean;
    gap?: string | number;
    gapLeft?: string | number;
    gapRight?: string | number;
    //TODO: Midlertidig løsning for å fikse https://github.com/navikt/aksel/issues/2402
    //Bør fjernes og sette tilbake div-er hvor style er brukt.
    style?: React.CSSProperties;
  }[];
  initialData: T;
  expandableRows?: (data: T[0], index: number) => ExpandableRowProps;
  simple?: boolean;
  hideRowsPerPage?: boolean;
  itemsToShow?: number;
  headerFieldIndex?: number;
  rowStyle?: (data: T[0], index: number) => React.CSSProperties;
  grouping?: Array<{
    filter: (data: T[0]) => boolean;
    component: ReactNode;
    startExpanded?: boolean;
    itemsToShow?: number;
    nonMinimizable?: boolean;
  }>;
  sortable?: boolean;
  controlsRef?: TableControls | undefined;
  onChangeState?: (state: SortedTableState<T>) => void;
  onSelectChange?: (selected: T) => void;
  // selectableId: (data: T[0], index?: number) => string | number
} & Partial<SortedTableProps<T>> & IfFirstThenBoth<{
  selectable?: boolean;
}, {
  selectableId?: (data: T[0], index: number) => string | number;
}>;
type TableControls = {
  openAll: (props?: any) => any;
  closeAll: (props?: any) => any;
};
const allRows = Symbol();
const noRows = Symbol();
export default function SortedTable<T extends unknown[]>(props: StableProps<T>) {
  const [internalState, setInternalState] = React.useState<SortedTableState<T> & {
    sortertData: T[number][];
    expandedRows: Array<boolean>;
  }>({
    orderBy: props.orderBy || '',
    direction: props.direction || undefined,
    page: props.page || 1,
    rowsPerPage: props.rowsPerPage || 50,
    sortertData: [...props.initialData],
    expandedGroups: props.grouping?.map(group => !!group.startExpanded) || [],
    expandedRows: props.expandableRows ? props.initialData.map((data, index) => props.expandableRows!(data, index).open) : new Array(props.initialData.length).fill(false),
    selectedRows: props.selectedRows || [] as unknown as T,
    selectedIds: props.selectedIds || []
  });
  const {
    initialData,
    fields,
    sortable = true,
    itemsToShow = props.initialData.length,
    selectable,
    selectableId = (_, index_0) => index_0,
    headerFieldIndex = 0
  } = props;
  const [visAlle, setVisAlle] = React.useState(false);
  const [antallVisninger, setAntallVisninger] = React.useState(1);
  const [visAlleForGruppe, _setVisAlleForGruppe] = React.useState(new Array(props.grouping?.length).fill(false));
  const {
    tekst
  } = store.useReducerState();
  function setVisAlleForGruppe(index_1: number, status: boolean) {
    const grupper = [...visAlleForGruppe];
    grupper[index_1] = status;
    _setVisAlleForGruppe(grupper);
  }
  if (props.controlsRef) {
    props.controlsRef['openAll'] = function () {
      handleStateChange({
        expandedRows: Array(props.initialData.length).fill(true)
      });
    };
    props.controlsRef['closeAll'] = function () {
      handleStateChange({
        expandedRows: Array(props.initialData.length).fill(false)
      });
    };
  }
  const {
    orderBy,
    direction,
    page,
    rowsPerPage,
    sortertData,
    expandedGroups,
    selectedRows,
    expandedRows
  } = internalState;
  const handleExpandRow = (open: boolean, index_2: number) => {
    const newExpandedRows = expandedRows.slice();
    newExpandedRows[index_2] = open;
    handleStateChange({
      expandedRows: newExpandedRows
    });
  };
  //const state = _state || internalState
  // const sortertData = internalState.sortertData || [...initialData]
  // If we provide an external state storage, then send all state changes externally because the component will be regenerated
  // at re-render with the new props. If not, update self-managed internal state directly.
  const handleStateChange = (newState: Partial<typeof internalState>) => {
    const combinedNewState = {
      ...internalState,
      ...newState
    };
    setInternalState(combinedNewState);
    props.onChangeState && props.onChangeState({
      orderBy: combinedNewState.orderBy,
      direction: combinedNewState.direction,
      page: combinedNewState.page,
      rowsPerPage: combinedNewState.rowsPerPage,
      expandedGroups: combinedNewState.expandedGroups,
      selectedRows: combinedNewState.selectedRows,
      selectedIds: combinedNewState.selectedIds
    }); //: setInternalState(combinedNewState)
  };
  const getNewSortDirection = (sortKey?: string, currentSortDirection: typeof direction = direction): typeof direction => {
    if (orderBy == sortKey) {
      // Hvis vi velger samme felt, gå gjennom sorteringstypene
      if (currentSortDirection === 'ascending') return 'descending';
      if (currentSortDirection === 'descending') return undefined;
      if (currentSortDirection === undefined) return 'ascending';
    } else {
      // Hvis vi felger nytt felt
      return 'ascending';
    }
  };
  const handleSort = (sortKey_0?: string, sortDirection: typeof direction = getNewSortDirection(sortKey_0, direction)) => {
    // 1 = stigende
    // -1 = synkende

    const sortertListe = [...initialData];
    const field_0 = fields.find(field => field.label == sortKey_0)!;
    const getFieldValue = field_0.sortValue || field_0.getValue;
    if (sortKey_0 && sortDirection) {
      sortertListe.sort((a, b) => {
        //@ts-ignore
        const valA = getFieldValue(a);
        //@ts-ignore
        const valB = getFieldValue(b);
        let result;
        if (!valA && !valB) {
          result = 0;
        } else if (!valB) {
          result = 1;
        } else if (!valA) {
          result = -1;
        } else {
          // Enkelte data kan ha mellomrom foran,derfor maa de trimmes
          result = String(valA).trim().localeCompare(String(valB).trim(), 'nb', {
            numeric: true
          });
        }
        return result;
      });
      if (sortDirection === 'descending') sortertListe.reverse();
    }
    handleStateChange({
      orderBy: sortDirection && sortKey_0 || '',
      //Hvis ingen retning
      direction: sortDirection,
      sortertData: sortertListe
    });
  };
  const handleSelectChange = (row: T[0] | Symbol) => {
    let newSelectedRows = new Array();
    if (selectable && selectableId) {
      const rowID = selectableId ? selectableId(row, 0) : 'none';
      if (row === allRows) newSelectedRows = [...sortertData];
      if (row === noRows) newSelectedRows = [];
      if (typeof rowID == 'number' || typeof rowID == 'string') {
        newSelectedRows = [...selectedRows];
        const alreadySelectedIndex = selectedRows.findIndex(f => selectableId(f, 0) === rowID);
        if (alreadySelectedIndex === -1) {
          newSelectedRows.push(row);
        } else newSelectedRows.splice(alreadySelectedIndex, 1);
      }
    }
    props.onSelectChange && props.onSelectChange(newSelectedRows as T);
    handleStateChange({
      selectedRows: newSelectedRows as T
    });
  };
  const data_0 = direction ? sortertData : initialData;
  const antallSynlige = visAlle ? data_0.length : Math.min(itemsToShow * antallVisninger, data_0.length);
  const antallGjenstaaende = data_0.length - antallSynlige;
  const visibleData = data_0?.slice(0, antallSynlige);

  // I simple mode finnes ingen paginering, så alle radene vises med mindre rowsPerPage er satt eksplisitt.
  const effectiveRowsPerPage = props.simple && !props.rowsPerPage ? visibleData.length : rowsPerPage;
  const antallSider = Math.ceil(sortertData.length / rowsPerPage);
  const TableRow = props.expandableRows ? Table.ExpandableRow : Table.Row;

  // Nullstille valgene når selectable endrer seg av og på fordi sortering og sånt ikke støttes under valg
  // Dette omgår at man velger elementer, huker av selectable, sorterer, og så går til valg igjen, da blir feil elementer valgt
  // oppdatering: ha alle som valgt som standard

  React.useEffect(() => {
    props.selectable && !props.selectedRows && handleSelectChange(allRows);
  }, [selectable]);

  // Hva er meningen men denne her?
  React.useEffect(() => {
    props.selectedRows && handleStateChange({
      selectedRows: props.selectedRows
    });
  }, [props.selectedRows]);
  React.useEffect(() => {
    orderBy && handleSort(orderBy, direction);
  }, []);
  React.useEffect(() => {
    handleStateChange({
      sortertData: props.initialData
    });
    orderBy && handleSort(orderBy, direction);
  }, [props.initialData]);
  return <div style={props.style} className={props.className}>
            {!props.simple && <div className={css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 'var(--ax-space-16)',
      marginBottom: 'var(--ax-space-12)',
      flexWrap: 'wrap'
    })}>
                    <BodyShort role="status">
                        {tekst.tabell.viserAvTotalt.replace('%start', String((page - 1) * rowsPerPage + 1)).replace('%slutt', String(Math.min(page * rowsPerPage, initialData.length))).replace('%total', String(initialData.length))}
                    </BodyShort>
                    {!props.hideRowsPerPage && <Select className={css({
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 'var(--ax-space-8)',
        '& .aksel-form-field__label': {
          marginBottom: 0
        }
      })} label={tekst.tabell.rader} size="small" value={rowsPerPage} onChange={e => handleStateChange({
        rowsPerPage: Number(e.target.value)
      })}>
                            {[5, 10, 20, 50, 100].map(value => <option key={value} value={value}>
                                    {value}
                                </option>)}
                        </Select>}
                    {antallSider > 1 && <Pagination className={css({
        marginLeft: 'auto'
      })} page={page} id="sideVelger" title="Sider" onPageChange={x => {
        handleStateChange({
          page: x
        });
        //setPage(x)
      }} count={antallSider} boundaryCount={1} siblingCount={1} size="small" prevNextTexts />}
                </div>}
            <div style={{
      overflowX: 'auto',
      overflowY: 'hidden'
    }}>
                <Table zebraStripes size={props.size} className="break-word-in-table" style={{
        tableLayout: 'fixed',
        minWidth: '100%',
        width: 'auto'
      }} {...direction && {
        sort: {
          orderBy,
          direction
        }
      }} onSortChange={sortKey_1 => handleSort(sortKey_1)}>
                    <Table.Header>
                        <Table.Row>
                            {props.expandableRows && <Table.ColumnHeader style={{
              padding: 0
            }} align="center">
                                    <Button size="xsmall" title={expandedRows.some(row_0 => row_0) ? tekst.tabell.lukkAlleRader : tekst.tabell.utvidAlleRader}
              //onClick={() => { expandedRows.some(row => row) ? setExpandedRows(expandedRows.fill(false)) : set}}
              onClick={() => handleStateChange({
                expandedRows: expandedRows.some(row_1 => row_1) ? Array(props.initialData.length).fill(false) : Array(props.initialData.length).fill(true)
              })} variant="tertiary-neutral" icon={expandedRows.some(row_2 => row_2) ? <ChevronUpDoubleCircleIcon aria-hidden style={{
                height: 28,
                width: 32
              }} /> : <ChevronDownDoubleCircleIcon aria-hidden style={{
                height: 28,
                width: 32
              }} />}></Button>
                                </Table.ColumnHeader>}
                            {selectable && <Table.DataCell style={{
              width: '0%'
            }}>
                                    <Checkbox checked={selectedRows.length == initialData.length} indeterminate={selectedRows.length > 0 && selectedRows.length < initialData.length} onChange={() => {
                selectedRows.length > 0 // Hvis minst ett er valgt
                ? handleSelectChange(noRows) // Tømme alle valg
                : handleSelectChange(allRows); // Huke av alle valg
              }} hideLabel>
                                        {tekst.tabell.velgAlleRader}
                                    </Checkbox>
                                </Table.DataCell>}
                            {fields.filter(field_1 => !field_1.hidden).map((field_2, index_3) => {
              const kanSorteres = field_2.sortable !== false && sortable && !!field_2.label && !field_2.hideLabel;
              return <Table.ColumnHeader key={index_3} align={field_2.align} role={field_2.hideLabel ? 'presentation' : undefined} title={typeof field_2.label == 'string' ? field_2.label : undefined} sortKey={typeof field_2.label === 'string' ? field_2.label : String(field_2.label)} sortable={kanSorteres} style={{
                width: field_2.width,
                minWidth: field_2.minWidth,
                hyphens: 'manual',
                paddingLeft: field_2.gap || field_2.gapLeft,
                paddingRight: field_2.gap || field_2.gapRight,
                ...field_2.labelStyle
              }}>
                                            {!field_2.hideLabel && (
                // En sorterbar header er selv en knapp, og kan derfor ikke inneholde hjelpeknappen.
                field_2.hjelpetekst && !kanSorteres ? <span className={css({
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8
                })}>
                                                        {field_2.label}
                                                        <HelpText title={hjelpetekstAriaLabel(tekst, typeof field_2.label === 'string' ? field_2.label : undefined)}>
                                                            {field_2.hjelpetekst}
                                                        </HelpText>
                                                    </span> : field_2.label)}
                                        </Table.ColumnHeader>;
            })}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {props.grouping ? props.grouping.map(group_0 => initialData.filter(group_0.filter)).map((filteredGroup, filteredGroupIndex) => [<tr key={filteredGroupIndex} style={{
            borderTop: '2px solid black'
          }}>
                                          <td colSpan={fields.length}>
                                              <Button variant="tertiary" onClick={() => !props.grouping![filteredGroupIndex].nonMinimizable && handleStateChange({
                expandedGroups: Object.assign(expandedGroups.slice(), {
                  [filteredGroupIndex]: !expandedGroups[filteredGroupIndex]
                })
              })} className={css({
                width: '100%',
                textAlign: 'left',
                display: 'flex !important',
                justifyContent: 'start !important',
                padding: '0.35rem 0 !important',
                '& .navds-label': {
                  display: 'flex !important',
                  alignItems: 'center'
                }
              })}>
                                                  {expandedGroups[filteredGroupIndex] ? <ChevronUpIcon role="presentation" opacity={props.grouping![filteredGroupIndex].nonMinimizable ? 0 : 1} /> : <ChevronDownIcon role="presentation" opacity={props.grouping![filteredGroupIndex].nonMinimizable ? 0 : 1} />}
                                                  {props.grouping![filteredGroupIndex].component}
                                              </Button>
                                          </td>
                                      </tr>, [filteredGroup.map((rad, radIndex) => {
            if (!(props.grouping![filteredGroupIndex].itemsToShow &&
            // Hvis vi har gitt maks antall elementer å vise
            radIndex >= props.grouping![filteredGroupIndex].itemsToShow! &&
            // og hvis raden vi er på er større enn maks antall elementer
            !visAlleForGruppe[filteredGroupIndex] //og vi ikke har skrudd på "vis alle"
            ))
              // så - vis raden
              return <Table.Row key={radIndex} //{selectableId(rad, radIndex)}
              className="break-word" style={{
                ...(expandedGroups[filteredGroupIndex] ? {} : {
                  display: 'none'
                }),
                ...(radIndex % 2 !== 0 && {
                  backgroundColor: 'white'
                }),
                ...(props.rowStyle ? props.rowStyle(rad, radIndex) : undefined)
              }}>
                                                          {selectable && <Table.DataCell>
                                                                  <Checkbox hideLabel checked={!!selectedRows.find((r, i) => selectableId(r, i) + '-' + radIndex === selectableId(rad, i) + '-' + radIndex)} onChange={() => handleSelectChange(rad)} aria-labelledby={`id-${rad}`}>
                                                                      {' '}
                                                                  </Checkbox>
                                                              </Table.DataCell>}
                                                          {fields.filter(field_3 => !field_3.hidden).map((field_4, fieldIndex) => {
                  const fieldValue = field_4.getValue(rad, radIndex, selectableId(rad, radIndex));
                  if (fieldIndex == headerFieldIndex) {
                    return <Table.HeaderCell key={selectableId(rad, radIndex) + '-' + fieldIndex} align={field_4.align} scope="row" style={{
                      //Soft shadow on right side to indicate scrollable area
                      boxShadow: field_4.sticky ? 'inset rgba(0, 0, 0, 0.125) -2px -2px 6px 0px' : undefined,
                      paddingLeft: field_4.gap || field_4.gapLeft,
                      paddingRight: field_4.gap || field_4.gapRight,
                      ...(field_4.sticky ? {
                        position: 'sticky',
                        left: 0,
                        background: 'inherit',
                        zIndex: 1
                      } : {})
                    }}>
                                                                              {field_4.maxLines ? <Clamp lines={field_4.maxLines}>{fieldValue}</Clamp> : fieldValue}
                                                                          </Table.HeaderCell>;
                  } else {
                    return <Table.DataCell key={selectableId(rad, radIndex) + '-' + fieldIndex} align={field_4.align} style={{
                      paddingLeft: field_4.gap || field_4.gapLeft,
                      paddingRight: field_4.gap || field_4.gapRight,
                      ...(field_4.sticky ? {
                        position: 'sticky',
                        left: 0,
                        background: 'inherit',
                        zIndex: 1
                      } : {}),
                      ...field_4.style
                    }}>
                                                                              {field_4.maxLines ? <Clamp lines={field_4.maxLines}>{fieldValue}</Clamp> : fieldValue}
                                                                          </Table.DataCell>;
                  }
                })}
                                                      </Table.Row>;
          }), filteredGroup && expandedGroups[filteredGroupIndex] && filteredGroup.length > (props.grouping![filteredGroupIndex].itemsToShow || filteredGroup.length) && <Table.Row key="blblb something" className={css({
            '&:hover': {
              backgroundColor: 'inherit !important'
            }
          })}>
                                                      <Table.DataCell colSpan={fields.length} align="center" style={{
              borderBottom: 'none'
            }}>
                                                          <Button variant="tertiary" size="small" onClick={() => setVisAlleForGruppe(filteredGroupIndex, !visAlleForGruppe[filteredGroupIndex])}>
                                                              {visAlleForGruppe[filteredGroupIndex] ? tekst.tabell.visMindre : tekst.tabell.visAlle.replace('%s', String(filteredGroup.length))}
                                                          </Button>
                                                      </Table.DataCell>
                                                  </Table.Row>]]) : [visibleData.slice((page - 1) * effectiveRowsPerPage, page * effectiveRowsPerPage).map((rad_0, radIndex_0) =>
          //@ts-expect-error - Row og ExandableRow har forskjellige type for content proppen som skaper problemer. Den klarer ikke å skjønne at content sendes kun når props.expandableRows er sant.
          <TableRow key={selectableId(rad_0, radIndex_0)} style={props.rowStyle ? props.rowStyle(rad_0, radIndex_0) : undefined} className="break-word"
          //expansionDisabled={!props.expandableRows}
          {...props.expandableRows && {
            ...props.expandableRows(rad_0, radIndex_0),
            open: expandedRows[radIndex_0],
            onOpenChange: open_0 => handleExpandRow(open_0, radIndex_0)
          }}>
                                          {selectable && <Table.DataCell>
                                                  <Checkbox hideLabel checked={!!selectedRows.find((r_0, i_0) => selectableId(r_0, i_0) === selectableId(rad_0, i_0))} onChange={() => handleSelectChange(rad_0)} aria-labelledby={`id-${rad_0}`}>
                                                      {' '}
                                                  </Checkbox>
                                              </Table.DataCell>}
                                          {fields.filter(field_5 => !field_5.hidden).map((field_6, fieldIndex_0) => {
              const fieldValue_0 = field_6.getValue(rad_0, radIndex_0, selectableId(rad_0, radIndex_0));
              if (fieldIndex_0 == headerFieldIndex) {
                return <Table.HeaderCell key={selectableId(rad_0, radIndex_0) + '-' + fieldIndex_0} align={field_6.align} scope="row" style={{
                  paddingLeft: field_6.gap || field_6.gapLeft,
                  paddingRight: field_6.gap || field_6.gapRight,
                  ...(field_6.sticky ? {
                    position: 'sticky',
                    left: 0,
                    background: 'inherit',
                    zIndex: 1
                  } : {}),
                  ...field_6.style
                }}>
                                                              {field_6.maxLines ? <Clamp lines={field_6.maxLines}>{fieldValue_0} </Clamp> : fieldValue_0}
                                                          </Table.HeaderCell>;
              } else {
                return <Table.DataCell key={selectableId(rad_0, radIndex_0) + '-' + fieldIndex_0} align={field_6.align} style={{
                  paddingLeft: field_6.gap || field_6.gapLeft,
                  paddingRight: field_6.gap || field_6.gapRight,
                  ...(field_6.sticky ? {
                    position: 'sticky',
                    left: 0,
                    background: 'inherit',
                    zIndex: 1
                  } : {}),
                  ...field_6.style
                }}>
                                                              {field_6.maxLines ? <Clamp lines={field_6.maxLines}>{fieldValue_0} </Clamp> : fieldValue_0}
                                                          </Table.DataCell>;
              }
            })}
                                      </TableRow>), data_0 && data_0.length > itemsToShow && <Table.Row key="blbl something" className={css({
            '&:hover': {
              backgroundColor: 'inherit !important'
            }
          })}>
                                          <Table.DataCell colSpan={fields.length} align="center" style={{
              borderBottom: 'none'
            }}>
                                              <HStack gap="space-8" justify="center">
                                                  {antallGjenstaaende > 0 && <Button variant="tertiary" size="small" onClick={() => setAntallVisninger(antallVisninger + 1)}>
                                                          {tekst.tabell.visFlere.replace('%s', String(Math.min(itemsToShow, antallGjenstaaende)))}
                                                      </Button>}
                                                  <Button variant="tertiary" size="small" onClick={() => {
                  if (antallGjenstaaende > 0) setVisAlle(true);else {
                    setVisAlle(false);
                    setAntallVisninger(1);
                  }
                }}>
                                                      {antallGjenstaaende > 0 ? tekst.tabell.visAlle.replace('%s', String(data_0.length)) : tekst.tabell.visMindre}
                                                  </Button>
                                              </HStack>
                                          </Table.DataCell>
                                      </Table.Row>]}
                    </Table.Body>
                </Table>
            </div>

            {!props.simple && <div style={{
      display: 'flex',
      alignItems: 'end',
      justifyContent: 'end',
      marginTop: 12
    }}>
                    <Pagination page={page} aria-labelledby="sideVelger" onPageChange={x_0 => {
        handleStateChange({
          page: x_0
        });
        // setPage(x)
      }} count={Math.ceil(sortertData.length / rowsPerPage)} boundaryCount={1} siblingCount={1} size="small" prevNextTexts />
                </div>}
        </div>;
}